import React, {PropsWithChildren, useContext, useEffect, useState, createContext} from "react";
import {fetchAuthSession, getCurrentUser} from "aws-amplify/auth";
import {get} from "lodash";
import {toast} from "sonner";
import {useAmplifyConfig} from "../hooks/useAmplifyConfig";
import {useAuthListener} from "../pages/auth/useAuthListener";
import {AuthSettings} from "../types/auth";
import {PartnersGroupItem} from "../declarations/server";

export interface AuthContextProps {
  email: string;
  setEmail: (value: string) => void;
  settings?: AuthSettings;
  setSettings: (setting?: AuthSettings) => void;
  error: string | null;
  fullName: string | undefined;
  setError: (error: string | null) => void;
  clearError: () => void;
  setIsLoggedIn: (value: boolean | undefined) => void;
  isLoggedIn: boolean | undefined;
  partnerCode: string | undefined;
  setPartnerCode: (p: string | undefined) => void;
  checkAuth: () => void;
  session: Awaited<ReturnType<typeof fetchAuthSession>> | null;
  username: string | undefined;
  partnersGroup: PartnersGroupItem[];
  setPartnersGroup: (group: PartnersGroupItem[]) => void;
  isMultiPartnerUser: boolean;
}

const AuthContext = createContext<AuthContextProps>(undefined!);

const AuthProvider = ({children}: PropsWithChildren) => {
  const [session, setSession] = useState<Awaited<ReturnType<typeof fetchAuthSession>> | null>(null);
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [fullName, setFullName] = useState<string | undefined>(undefined);
  const [settings, setSettings] = useState<AuthSettings | undefined>(undefined);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | undefined>(undefined);
  const [partnerCode, setPartnerCode] = useState<string | undefined>(undefined);
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [partnersGroup, setPartnersGroup] = useState<PartnersGroupItem[]>([]);
  useAuthListener();
  const clearError = () => setError(null);
  const initConfig = useAmplifyConfig();

  const checkAuth = async () => {
    try {
      const authSession = await fetchAuthSession();
      setSession(authSession);
      if (!authSession.tokens) {
        setIsLoggedIn(false);
        return;
      }
      setIsLoggedIn(true);
    } catch (error) {
      setIsLoggedIn(false);
    }
  };

  const initAuthDetails = async () => {
    try {
      const session = await fetchAuthSession();
      if (session.tokens?.idToken) {
        const name = get(session.tokens.idToken.payload, "name");
        const lastName = get(session.tokens.idToken.payload, "family_name");
        if (name && lastName) {
          setFullName(name + " " + lastName);
        }
        const userEmail = get(session.tokens.idToken.payload, "email");
        setEmail(String(userEmail));
      }
      const {username: currentUsername} = await getCurrentUser();
      setUsername(currentUsername);
    } catch (error) {}
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    initAuthDetails();
  }, [session]);

  useEffect(() => {
    if (initConfig) {
      checkAuth();
    }
  }, [initConfig]);

  const isMultiPartnerUser = partnersGroup.length > 1;

  return (
    <AuthContext.Provider
      value={{
        email,
        setEmail,
        settings,
        setSettings,
        error,
        setError,
        clearError,
        fullName,
        isLoggedIn,
        setIsLoggedIn,
        partnerCode,
        setPartnerCode: setPartnerCode,
        checkAuth,
        session,
        username,
        partnersGroup,
        setPartnersGroup,
        isMultiPartnerUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuthContext = (): AuthContextProps => {
  const context = useContext(AuthContext);

  if (!context) throw new Error("AuthContext must be used inside AuthProvider");

  return context;
};
export {AuthProvider, useAuthContext};
